import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, PenLine, Volume2, UserRound, LogOut, Menu, X, Sun, Moon } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';

const LANGS = [
  { code: 'uz', label: "O'z" },
  { code: 'ru', label: 'Ru' },
  { code: 'en', label: 'En' },
];

function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [menuOpen, setMenuOpen] = useState(false);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
      isActive
        ? 'bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-200'
        : 'text-stone-600 hover:text-brand-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:text-brand-300 dark:hover:bg-stone-800'
    }`;

  const navLinks = user && (
    <>
      <NavLink to="/lessons" className={navLinkClass} onClick={() => setMenuOpen(false)}>
        <BookOpen size={16} strokeWidth={2} />
        {t('nav.lessons')}
      </NavLink>
      <NavLink to="/write" className={navLinkClass} onClick={() => setMenuOpen(false)}>
        <PenLine size={16} strokeWidth={2} />
        {t('nav.write')}
      </NavLink>
      <NavLink to="/listen" className={navLinkClass} onClick={() => setMenuOpen(false)}>
        <Volume2 size={16} strokeWidth={2} />
        {t('nav.listen')}
      </NavLink>
      <NavLink to="/profile" className={navLinkClass} onClick={() => setMenuOpen(false)}>
        <UserRound size={16} strokeWidth={2} />
        {t('nav.profile')}
      </NavLink>
    </>
  );

  const ThemeToggleButton = ({ className }) => (
    <button
      onClick={toggleTheme}
      aria-label="Toggle theme"
      className={`p-2 rounded-full text-stone-500 hover:text-brand-700 hover:bg-stone-100 dark:text-stone-400 dark:hover:text-brand-300 dark:hover:bg-stone-800 transition-colors ${className || ''}`}
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );

  return (
    <nav className="flex-shrink-0 bg-white/90 dark:bg-stone-900/90 backdrop-blur border-b border-stone-200 dark:border-stone-800">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-[72px]">
          <Link to="/" className="flex items-center gap-2 font-display text-xl font-semibold text-brand-800 dark:text-brand-200">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700 text-white text-sm rotate-45">
              <span className="-rotate-45">ع</span>
            </span>
            {t('app_name')}
          </Link>

          <div className="hidden md:flex items-center gap-1">{navLinks}</div>

          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-0.5 bg-stone-100 dark:bg-stone-800 rounded-full p-0.5">
              {LANGS.map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => changeLanguage(code)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                    i18n.language === code
                      ? 'bg-white dark:bg-stone-700 text-brand-800 dark:text-brand-200 shadow-sm'
                      : 'text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <ThemeToggleButton />

            {user ? (
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-stone-600 hover:text-red-600 hover:bg-red-50 dark:text-stone-300 dark:hover:text-red-400 dark:hover:bg-red-950/40 transition-colors"
              >
                <LogOut size={16} strokeWidth={2} />
                {t('nav.logout')}
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-full text-sm font-semibold text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-stone-800 transition-colors"
                >
                  {t('auth.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-brand-700 text-white hover:bg-brand-800 transition-colors"
                >
                  {t('auth.register')}
                </Link>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1">
            <ThemeToggleButton />
            <button
              className="p-2 text-stone-600 dark:text-stone-300"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden pb-4 flex flex-col gap-3 border-t border-stone-100 dark:border-stone-800 pt-3">
            <div className="flex flex-col gap-1">{navLinks}</div>
            <div className="flex items-center gap-0.5 bg-stone-100 dark:bg-stone-800 rounded-full p-0.5 w-fit">
              {LANGS.map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => changeLanguage(code)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                    i18n.language === code
                      ? 'bg-white dark:bg-stone-700 text-brand-800 dark:text-brand-200 shadow-sm'
                      : 'text-stone-500 dark:text-stone-400'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {user ? (
              <button
                onClick={logout}
                className="flex items-center gap-1.5 text-sm font-semibold text-stone-600 dark:text-stone-300"
              >
                <LogOut size={16} strokeWidth={2} />
                {t('nav.logout')}
              </button>
            ) : (
              <div className="flex gap-2">
                <Link to="/login" className="px-4 py-2 rounded-full text-sm font-semibold text-brand-700 bg-brand-50 dark:text-brand-300 dark:bg-stone-800">
                  {t('auth.login')}
                </Link>
                <Link to="/register" className="px-4 py-2 rounded-full text-sm font-semibold bg-brand-700 text-white">
                  {t('auth.register')}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
